import { OntarioHealthNumberSchema } from "@emr/contracts";
import { useEffect, useRef, useState } from "react";
import { Icon } from "../Icon";
import { RequiredMarker } from "../RequiredMarker";
import { BookingChallenge } from "./BookingChallenge";
import { DemoBookingApiError } from "./demoSchedulingClient";
import type { PatientSchedulingClient } from "./publicSchedulingClient";

/**
 * An existing patient's booking pass and the date of birth it was issued for. The page holds it
 * in memory only (never a URL or browser storage) and drops it when the session ends.
 */
export interface VerifiedPatient {
  readonly verification: string;
  readonly dateOfBirth: string;
  /** The legal first name, given only when exactly one patient matched. */
  readonly firstName?: string;
  readonly expiresAt: string;
}

type Notice = "failed" | "expired" | "challenge" | "busy" | "unavailable" | null;

const noticeText: Record<Exclude<Notice, null>, readonly [string, string]> = {
  // Every refusal reads the same whatever its cause, so the page never hints whether a patient
  // with these details exists.
  failed: ["We couldn't verify your details.", "Check them and try again, or call the clinic."],
  expired: ["Your verification expired.", "Confirm your details again."],
  challenge: ["The security check expired.", "Complete it again, then continue."],
  busy: ["Too many attempts.", "Wait a few minutes and try again, or call the clinic."],
  unavailable: [
    "Booking is temporarily unavailable.",
    "Please try again, or call the clinic to book.",
  ],
};

export function VerifyPatientStep({
  verify,
  turnstileSiteKey,
  today,
  expired,
  onVerified,
  onLocked,
  onUnavailable,
}: {
  readonly verify: NonNullable<PatientSchedulingClient["verify"]>;
  readonly turnstileSiteKey: string | undefined;
  /** The latest date of birth the form accepts (the patient's local today). */
  readonly today: string;
  /** The previous pass ran out before the booking was made. */
  readonly expired: boolean;
  readonly onVerified: (patient: VerifiedPatient) => void;
  /** The attempts for these details are used up: the patient has to call. */
  readonly onLocked: () => void;
  /** The clinic no longer verifies online (for example booking was switched off). */
  readonly onUnavailable: () => void;
}) {
  const [healthNumber, setHealthNumber] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [challengeToken, setChallengeToken] = useState("");
  const [challengeAttempt, setChallengeAttempt] = useState(0);
  const [pending, setPending] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [notice, setNotice] = useState<Notice>(expired ? "expired" : null);
  const operation = useRef<AbortController | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
    return () => operation.current?.abort();
  }, []);
  useEffect(() => {
    if (errors.length > 0 || (notice !== null && notice !== "expired")) alertRef.current?.focus();
  }, [errors, notice]);

  const submit = async () => {
    if (operation.current) return;
    const healthNumberResult = OntarioHealthNumberSchema.safeParse(healthNumber);
    const problems = [
      healthNumberResult.success
        ? ""
        : (healthNumberResult.error.issues[0]?.message ?? "Check the health card number"),
      !dateOfBirth
        ? "Date of birth is required"
        : dateOfBirth > today
          ? "Date of birth cannot be in the future"
          : "",
      turnstileSiteKey && !challengeToken ? "Complete the security check before continuing." : "",
    ].filter(Boolean);
    if (problems.length > 0) {
      setErrors(problems);
      return;
    }
    const current = new AbortController();
    operation.current = current;
    setPending(true);
    setErrors([]);
    setNotice(null);
    try {
      const result = await verify({ healthNumber, dateOfBirth }, challengeToken, current.signal);
      if (current.signal.aborted) return;
      if (result.verified) {
        onVerified({
          verification: result.verification,
          dateOfBirth,
          expiresAt: result.expiresAt,
          ...(result.firstName === undefined ? {} : { firstName: result.firstName }),
        });
      } else if (result.locked) {
        onLocked();
      } else {
        setNotice("failed");
      }
    } catch (error) {
      if (current.signal.aborted) return;
      const status = error instanceof DemoBookingApiError ? error.status : 0;
      // The clinic stopped verifying online (booking off, or open to anyone): reload the page's
      // settings so it shows what applies now.
      if (status === 404) onUnavailable();
      setNotice(
        error instanceof DemoBookingApiError && error.code === "CHALLENGE_FAILED"
          ? "challenge"
          : status === 429
            ? "busy"
            : "unavailable",
      );
    } finally {
      if (operation.current === current) operation.current = null;
      if (!current.signal.aborted) {
        // A security-check token is good for one request.
        setPending(false);
        setChallengeToken("");
        setChallengeAttempt((attempt) => attempt + 1);
      }
    }
  };

  const message = notice === null ? null : noticeText[notice];
  return (
    <>
      {errors.length > 0 ? (
        <div className="error-summary" role="alert" tabIndex={-1} ref={alertRef}>
          <strong>Please check the following:</strong>
          <ul>
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      ) : message !== null ? (
        <div className="error-summary" role="alert" tabIndex={-1} ref={alertRef}>
          <strong>{message[0]}</strong>
          <p>{message[1]}</p>
        </div>
      ) : null}
      <form
        className="booking-card"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <header className="booking-step-header">
          <h1 ref={headingRef} tabIndex={-1}>
            Confirm your details
          </h1>
          <span>Online booking is for existing patients of the clinic.</span>
        </header>
        <div className="booking-form-grid booking-form-grid-two">
          <label>
            <span>
              Ontario health card number
              <RequiredMarker />
            </span>
            <input
              autoComplete="off"
              inputMode="numeric"
              maxLength={14}
              required
              value={healthNumber}
              onChange={(event) => {
                setHealthNumber(event.target.value);
                if (errors.length > 0) setErrors([]);
              }}
            />
            <small>The 10 digits on the front of your card.</small>
          </label>
          <label>
            <span>
              Date of birth
              <RequiredMarker />
            </span>
            <input
              type="date"
              autoComplete="off"
              required
              max={today}
              value={dateOfBirth}
              onChange={(event) => {
                setDateOfBirth(event.target.value);
                if (errors.length > 0) setErrors([]);
              }}
            />
          </label>
        </div>
        <p className="booking-time-note booking-verify-note">
          <Icon name="person" />
          New to the clinic? Please call the clinic to register.
        </p>
        {turnstileSiteKey ? (
          <BookingChallenge
            key={challengeAttempt}
            siteKey={turnstileSiteKey}
            onToken={setChallengeToken}
          />
        ) : null}
        <footer className="booking-actions">
          <button
            type="submit"
            className="continue-button"
            disabled={pending || (Boolean(turnstileSiteKey) && !challengeToken)}
          >
            {pending ? (
              <>
                <span className="button-spinner" />
                Checking…
              </>
            ) : (
              <>
                Continue
                <Icon name="arrow" />
              </>
            )}
          </button>
        </footer>
      </form>
    </>
  );
}
